import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster } from "sonner";
import { ArrowLeft, Bath, BedDouble, CalendarDays, Check, MapPin, MessageCircle, Palmtree, Users } from "lucide-react";
import { useSite, waLink, BRAND } from "@/lib/site";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { useTours } from "@/components/ToursSection";
import { BRL } from "@/components/PixPayment";
import { AdaptiveGallery, ShareBar, shareLink } from "@/components/ShareGallery";
import { BookingModal } from "./PublicSite";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtBR = (s) => s.split("-").reverse().join("/");
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export const propertyWhatsAppText = (p) =>
  `Olá, equipe Se Hospede AKI! Tenho interesse no imóvel "${p.title}" (${p.city}/${p.state}) e gostaria de tirar algumas dúvidas antes de reservar:\n\n` +
  "• Disponibilidade para as minhas datas\n• Valor total da estadia\n• Horários de check-in e check-out\n\nAguardo o retorno. Obrigado(a)!";

export default function PropertyPage({ id }) {
  const [p, setP] = useState(undefined);
  const [galleryAt, setGalleryAt] = useState(null);
  const [booking, setBooking] = useState(false);
  const tours = useTours();
  const site = useSite();

  useEffect(() => {
    axios.get(`${API}/public/properties/${id}`).then((r) => {
      setP(r.data);
      document.title = `${r.data.title} — ${BRAND}`;
    }).catch(() => setP(null));
  }, [id]);

  const wa = p && waLink(site.whatsapp, propertyWhatsAppText(p));
  const seasons = (p?.season_prices || []).filter((s) => s.end >= new Date().toLocaleDateString("sv-SE"));

  return (
    <div className="min-h-screen bg-[#FBF8F3] text-slate-800" style={{ fontFamily: "Outfit, system-ui, sans-serif" }}>
      <Toaster position="top-center" richColors />
      <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <span className="bg-[#0A66C2] p-1.5 rounded-lg"><Palmtree className="text-white w-5 h-5" /></span>
            <span className="text-lg font-extrabold text-[#0A66C2] whitespace-nowrap">Se Hospede <span className="text-[#FFB800]">AKI</span></span>
          </a>
          <a href="/#imoveis" className="text-sm font-semibold text-slate-500 hover:text-[#0A66C2] flex items-center gap-1" data-testid="property-page-back"><ArrowLeft size={14} /> Todos os imóveis</a>
        </div>
      </header>

      {p === undefined && <p className="text-center py-24 text-slate-400">Carregando...</p>}
      {p === null && (
        <div className="text-center py-24" data-testid="property-not-found">
          <p className="text-xl font-bold mb-2">Imóvel não encontrado</p>
          <a href="/#imoveis" className="text-[#0A66C2] font-semibold">Ver imóveis disponíveis</a>
        </div>
      )}
      {p && (
        <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12" data-testid="property-page">
          <h1 className="text-3xl sm:text-5xl font-bold text-slate-900 leading-tight mb-2" style={{ fontFamily: "Fraunces, serif" }}>{p.title}</h1>
          <p className="text-slate-500 flex items-center gap-1.5 mb-6"><MapPin size={16} className="text-[#0A66C2]" /> {p.city}, {p.state}</p>
          <AdaptiveGallery images={p.images || []} title={p.title} onOpen={setGalleryAt} testId="property-page-gallery" />
          {galleryAt !== null && <PhotoLightbox images={p.images} title={p.title} start={galleryAt} onClose={() => setGalleryAt(null)} />}

          <div className="grid lg:grid-cols-3 gap-10 mt-10">
            <div className="lg:col-span-2 space-y-8">
              <div className="flex flex-wrap gap-3">
                <span className="bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm flex items-center gap-2"><Users size={16} className="text-[#0A66C2]" /> até {plural(p.max_guests, "hóspede", "hóspedes")}</span>
                <span className="bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm flex items-center gap-2"><BedDouble size={16} className="text-[#0A66C2]" /> {plural(p.bedrooms, "quarto", "quartos")}</span>
                <span className="bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm flex items-center gap-2"><Bath size={16} className="text-[#0A66C2]" /> {plural(p.bathrooms, "banheiro", "banheiros")}</span>
              </div>
              {p.description && (
                <section>
                  <h2 className="text-xl font-bold text-slate-900 mb-3">Sobre o imóvel</h2>
                  <p className="text-base text-slate-600 leading-relaxed whitespace-pre-line" data-testid="property-page-description">{p.description}</p>
                </section>
              )}
              {p.amenities?.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold text-slate-900 mb-3">O que o lugar oferece</h2>
                  <ul className="grid sm:grid-cols-2 gap-2" data-testid="property-page-amenities">
                    {p.amenities.map((a) => <li key={a} className="flex items-center gap-2 text-slate-600"><Check size={16} className="text-emerald-600" /> {a}</li>)}
                  </ul>
                </section>
              )}
              {seasons.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold text-slate-900 mb-3">Tarifas de temporada</h2>
                  <ul className="space-y-2">
                    {seasons.map((s) => (
                      <li key={s.start + s.label} className="flex items-center justify-between bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm">
                        <span className="flex items-center gap-2"><CalendarDays size={16} className="text-amber-600" /> {s.label} · {fmtBR(s.start)} a {fmtBR(s.end)}</span>
                        <strong className="text-slate-800">{BRL(s.price)}/noite</strong>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              <section>
                <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">Compartilhe este imóvel</h2>
                <ShareBar shareUrl={shareLink("casa", p.id)} text={`${p.title} — ${p.city}/${p.state}. Veja as fotos e reserve pelo ${BRAND}:`} testPrefix="property" />
              </section>
            </div>
            <aside className="lg:sticky lg:top-24 h-fit bg-white rounded-[2rem] border border-slate-200 shadow-sm p-6" data-testid="property-page-booking-card">
              <p className="text-slate-400 text-xs font-bold uppercase">Diária a partir de</p>
              <p className="text-4xl font-extrabold text-[#0A66C2] mb-1">{BRL(p.price_per_night)}</p>
              <p className="text-sm text-slate-500 mb-6">Sinal de {p.deposit_percent}% via PIX · restante no check-in</p>
              <button onClick={() => setBooking(true)} className="w-full bg-[#FFCC00] text-blue-900 py-4 rounded-2xl font-extrabold text-lg shadow-[0_4px_0_#b38f00] active:translate-y-1 active:shadow-none transition-transform mb-3" data-testid="property-page-book">
                Ver datas e reservar
              </button>
              {wa && <a href={wa} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-[#25D366] text-[#128C4A] font-bold" data-testid="property-page-whatsapp"><MessageCircle size={18} /> Tirar dúvidas no WhatsApp</a>}
            </aside>
          </div>
        </main>
      )}
      {booking && p && <BookingModal property={p} tours={tours} onClose={() => setBooking(false)} />}
    </div>
  );
}
